import { useState, useEffect } from "react";

// Global variable to hold the deferred prompt event
let _deferredPrompt = null;

if (typeof window !== "undefined") {
  // Capture the beforeinstallprompt event when it fires
  window.addEventListener("beforeinstallprompt", (e) => {
    // Prevent the browser's default install UI from showing immediately
    e.preventDefault();

    // Store the event for later use
    _deferredPrompt = e;

    // Dispatch a custom event to notify React components that install is available
    window.dispatchEvent(new Event("pwa-install-available"));
    console.log("✅ beforeinstallprompt captured");
  });
}

/**
 * Checks if the current environment is a restrictive in-app browser.
 * @returns {boolean} True if inside a known in-app browser.
 */
function isInAppBrowser() {
  if (typeof navigator === "undefined") return false;

  // FIX: Remove deprecated 'navigator.vendor' to clear the TypeScript warning.
  // The User Agent string is sufficient for modern in-app browser detection.
  const ua = navigator.userAgent || window.opera;

  return [
    "FBAN",
    "FBAV",
    "Messenger",
    "Instagram",
    "Twitter",
    "Snapchat",
    "LinkedInApp",
  ].some((key) => ua.includes(key));
}

/**
 * Checks if the current device is iOS (iPhone/iPad/iPod).
 * @returns {boolean} True if running on iOS.
 */
function isiOS() {
  if (typeof navigator === "undefined") return false;
  // Note: navigator.standalone is an iOS-specific proprietary property
  return /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Custom hook to manage PWA installation state and logic.
 * @returns {object} PWA status and install handler.
 */
export function usePWA() {
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [showInAppWarning, setShowInAppWarning] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [installFailed, setInstallFailed] = useState(false);
  const [showManualInstructions, setShowManualInstructions] = useState(false);

  useEffect(() => {
    const updateInstallState = async () => {
      // Check for standalone mode (installed PWA)
      const isStandalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        navigator.standalone === true;

      // Check for installed related native apps (requires manifest configuration)
      let isRelatedAppInstalled = false;
      if (
        "getInstalledRelatedApps" in navigator &&
        window.top === window.self
      ) {
        try {
          const relatedApps = await navigator.getInstalledRelatedApps();
          isRelatedAppInstalled = relatedApps.length > 0;
        } catch (err) {
          console.warn("getInstalledRelatedApps failed", err);
        }
      }

      setIsAppInstalled(isStandalone || isRelatedAppInstalled);
    };

    updateInstallState();

    // Monitor for changes in display mode (e.g., app installed/uninstalled)
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    mediaQuery.addEventListener?.("change", updateInstallState);

    // Event listener for when the app installation completes
    window.addEventListener("appinstalled", () => {
      console.log("✅ App installed");
      setIsAppInstalled(true);
      setCanInstall(false);
      _deferredPrompt = null;
    });

    // Custom event listener triggered when beforeinstallprompt fires
    window.addEventListener("pwa-install-available", () => {
      setCanInstall(true);
    });

    return () => {
      mediaQuery.removeEventListener?.("change", updateInstallState);
    };
  }, []);

  const handleInstallClick = async () => {
    setInstallFailed(false); // Reset failure state
    setShowInAppWarning(false); // Reset previous warnings
    setShowIOSInstructions(false); // Reset previous instructions
    setShowManualInstructions(false); // Reset previous manual instructions

    // 1. In-app browser check
    if (isInAppBrowser()) {
      setShowInAppWarning(true);
      return;
    }

    // 2. iOS check
    if (isiOS()) {
      setShowIOSInstructions(true);
      return;
    }

    // 3. Already installed check (Re-check on click)
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      navigator.standalone === true;

    let isRelatedAppInstalled = false;
    if ("getInstalledRelatedApps" in navigator && window.top === window.self) {
      try {
        const relatedApps = await navigator.getInstalledRelatedApps();
        isRelatedAppInstalled = relatedApps.length > 0;
      } catch (err) {
        console.warn("getInstalledRelatedApps failed", err);
      }
    }

    if (isStandalone || isRelatedAppInstalled) {
      setIsAppInstalled(true);
      setCanInstall(false);
      _deferredPrompt = null;
      return;
    }

    // 4. Android/Chrome install logic
    if (_deferredPrompt) {
      try {
        _deferredPrompt.prompt();
        const result = await _deferredPrompt.userChoice;

        // CRITICAL: Always clear the prompt after use (browser invalidates it)
        _deferredPrompt = null;

        if (result.outcome === "accepted") {
          console.log("✅ Install prompt accepted");
          setIsAppInstalled(true);
          setCanInstall(false);
        } else {
          console.log("❌ Install prompt dismissed");
          // Browser will fire beforeinstallprompt again later (after cooldown)
          setCanInstall(false); // Temporarily hide button until new prompt fires
        }
      } catch (error) {
        console.error("Install prompt error:", error);
        _deferredPrompt = null;
        setInstallFailed(true);
      }
    } else {
      // Show manual install instructions instead of failing silently
      setShowManualInstructions(true);
      console.log("📱 Showing manual install instructions");
    }
  };

  return {
    isAppInstalled,
    canInstall,
    handleInstallClick,
    showInAppWarning,
    setShowInAppWarning,
    showIOSInstructions,
    setShowIOSInstructions,
    showManualInstructions,
    setShowManualInstructions,
    installFailed,
  };
}
